import { type FC, type PropsWithChildren, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'

import {
  Accordion,
  AccordionGroup,
  Body1,
  Link,
  List,
  ListItem,
  ListItemText,
  Paper,
  useIsMobileVariant,
} from '@dhl-ui/components'
import { ChevronRightIcon } from '@dhl-ui/icons'
import { NativeModal } from '@dhl/native-modal'

const InfoSectionList = ({ children }: PropsWithChildren) => (
  <Body1 component="ul" sx={{ ml: -2.5, pb: 3 }}>
    {children}
  </Body1>
)

const ThingsToMind = () => {
  const { t } = useTranslation()

  return (
    <InfoSectionList>
      {(
        t('portal.profile.preferences.setupPage.agreedPlace.thingsToRememberText', {
          returnObjects: true,
        }) as string[]
      ).map((row, index) => (
        // eslint-disable-next-line react/no-array-index-key -- we don't have a unique key available
        <li key={index}>{row}</li>
      ))}
    </InfoSectionList>
  )
}

const PerfectSpotTips = () => {
  const { t } = useTranslation()

  return (
    <InfoSectionList>
      {(
        t('portal.profile.preferences.setupPage.agreedPlace.perfectSpotTipsText', {
          returnObjects: true,
        }) as string[]
      ).map((_, index) => (
        // eslint-disable-next-line react/no-array-index-key -- we don't have a unique key available
        <li key={index}>
          <Trans
            // @ts-ignore -- dynamic translation key, we know it exists
            i18nKey={`portal.profile.preferences.setupPage.agreedPlace.perfectSpotTipsText.${index}`}
            components={{
              faqLink: (
                <Link
                  external
                  href={t('portal.profile.preferences.setupPage.agreedPlace.faqURL')}
                  title="faq"
                  rel="noopener noreferrer"
                  target="_blank"
                />
              ),
            }}
          />
        </li>
      ))}
    </InfoSectionList>
  )
}

const DesktopVariant = () => {
  const { t } = useTranslation()

  return (
    <AccordionGroup isOutlined isRounded>
      <Accordion hasDivider title={t('portal.profile.preferences.setupPage.agreedPlace.thingsToMindButton')}>
        <ThingsToMind />
      </Accordion>

      <Accordion title={t('portal.profile.preferences.setupPage.agreedPlace.perfectSpotTipsButton')}>
        <PerfectSpotTips />
      </Accordion>
    </AccordionGroup>
  )
}

const MobileVariant = () => {
  const { t } = useTranslation()
  const [modalType, setModalType] = useState<'thingsToMind' | 'perfectSpotTips' | undefined>()

  return (
    <>
      <Paper isOutlined>
        <List>
          <ListItem
            isButton
            alignItems="center"
            slotProps={{
              secondaryIcon: <ChevronRightIcon />,
            }}
            onClick={() => setModalType('thingsToMind')}
          >
            <ListItemText emphasis primary={t('portal.profile.preferences.setupPage.agreedPlace.thingsToMindButton')} />
          </ListItem>
          <ListItem
            isButton
            alignItems="center"
            slotProps={{
              secondaryIcon: <ChevronRightIcon />,
            }}
            onClick={() => setModalType('perfectSpotTips')}
          >
            <ListItemText
              emphasis
              primary={t('portal.profile.preferences.setupPage.agreedPlace.perfectSpotTipsButton')}
            />
          </ListItem>
        </List>
      </Paper>

      <NativeModal isSwipeable isFullscreen={false} open={!!modalType} onClose={() => setModalType(undefined)}>
        <NativeModal.Header
          title={
            modalType === 'thingsToMind'
              ? t('portal.profile.preferences.setupPage.agreedPlace.thingsToMindButton')
              : t('portal.profile.preferences.setupPage.agreedPlace.perfectSpotTipsButton')
          }
          onClose={() => setModalType(undefined)}
        />
        <NativeModal.Content>
          {modalType === 'thingsToMind' ? <ThingsToMind /> : <PerfectSpotTips />}
        </NativeModal.Content>
      </NativeModal>
    </>
  )
}

export const AgreedPlaceAdditionalInformation: FC = () => {
  const isMobileVariant = useIsMobileVariant()

  return isMobileVariant ? <MobileVariant /> : <DesktopVariant />
}
